/**
 * Calls to the backend. Everything goes through /api on the same origin (nginx proxies it), with
 * the session cookie. State-changing requests send the CSRF token from the XSRF-TOKEN cookie.
 */

export class ApiError extends Error {
  readonly status: number;
  /** The backend's problem `code`, e.g. "fileTooLarge". The UI translates it. */
  readonly code: string | undefined;
  /** How long to wait before trying again, when the server says so (e.g. after failed sign-ins). */
  readonly retryAfterSeconds: number | undefined;

  constructor(status: number, code: string | undefined, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

interface Problem {
  status?: number;
  detail?: string;
  code?: string;
  retryAfterSeconds?: number;
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return undefined;
}

async function csrfToken(): Promise<string | undefined> {
  if (!readCookie('XSRF-TOKEN')) {
    await fetch('/api/auth/csrf', { credentials: 'same-origin' });
  }
  return readCookie('XSRF-TOKEN');
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  json?: unknown;
  form?: FormData;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.json);
  } else if (options.form) {
    body = options.form;
  }
  if (method !== 'GET') {
    const token = await csrfToken();
    if (token) headers['X-XSRF-TOKEN'] = token;
  }

  const response = await fetch(path, { method, headers, body, credentials: 'same-origin' });
  if (!response.ok) {
    let problem: Problem = {};
    try {
      problem = (await response.json()) as Problem;
    } catch {
      // Not a problem document (e.g. a proxy error page).
    }
    throw new ApiError(response.status, problem.code, problem.detail ?? response.statusText, problem.retryAfterSeconds);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
